import { ExternalLink } from 'lucide-react-native';
import { Pressable, StyleSheet, View } from 'react-native';

import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';
import { openWebUrl } from '@/lib/openWebUrl';

export type TrackDetailsCardProps = {
  carrier?: string | null;
  trackingNumber?: string | null;
  /** The carrier's tracking page (http(s) only; openWebUrl refuses anything else). */
  url?: string | null;
  testID?: string;
};

/**
 * Carrier, tracking number and "View on carrier site" (Figma "Track"). No
 * "Ship to" row: the address is never stored and Voltr doesn't send it back.
 * Renders nothing until the brand has given us at least one of the three.
 */
export function TrackDetailsCard({ carrier, trackingNumber, url, testID }: TrackDetailsCardProps) {
  const rows = [
    { label: 'Carrier', value: carrier },
    { label: 'Tracking Number', value: trackingNumber },
  ].filter((row): row is { label: string; value: string } => !!row.value);
  if (rows.length === 0 && !url) return null;

  return (
    <View style={styles.card} testID={testID}>
      {rows.map((row, i) => (
        <View key={row.label} style={[styles.row, i > 0 && styles.divider]}>
          <Text variant="caption" color="textMuted">
            {row.label}
          </Text>
          <Text variant="bodyBold" color="deepGreen" align="right" selectable style={styles.value}>
            {row.value}
          </Text>
        </View>
      ))}
      {url ? (
        <Pressable
          onPress={() => openWebUrl(url)}
          accessibilityRole="link"
          accessibilityLabel="View on carrier site"
          style={({ pressed }) => [styles.row, rows.length > 0 && styles.divider, pressed && styles.pressed]}
          testID={testID ? `${testID}-carrier-site` : undefined}
        >
          <Text variant="bodyBold" color="deepGreen">
            View on carrier site
          </Text>
          <ExternalLink size={18} color={colors.deepGreen} strokeWidth={2} />
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: radii.lg,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    minHeight: 48,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
  },
  divider: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  // A 22-digit USPS number wraps instead of pushing the label off the row.
  value: { flexShrink: 1 },
  pressed: { opacity: 0.7 },
});
